import { createSlice } from "@reduxjs/toolkit";
import { logOut } from "../auth/authSlice";
import {
  getItemReviews,
  getMyReviews,
  submitReview,
  removeReview,
} from "./reviewsActions";

const initialState = {
  loading: false, // My Reviews page list fetch
  error: null,
  myReviews: [], // current page of the My Reviews list (results)
  myReviewsMeta: null, // pagination meta from the server
  itemReviews: [], // current page of one item's reviews (results)
  itemReviewsMeta: null, // pagination meta from the server
  itemReviewsLoading: false,
  submitting: false, // a submit/delete is in flight
  // { [menuItemId]: { has_review, rating, comment, rate, reviews_count } } —
  // recorded after submits/deletes this session. `rating`/`comment` are the
  // customer's own review (feeds the reviews tab); `rate`/`reviews_count`
  // are the FRESH item average the server returns with the write — the
  // catalog's `rate` in the menuItems slice goes stale after a submit,
  // exactly the problem the favorites `overrides` map solves; star widgets
  // show this average when present.
  overrides: {},
};

const reviewsSlice = createSlice({
  name: "reviews",
  initialState,
  reducers: {
    resetReviewsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const applyRejected = (state, action) => {
      state.loading = false;
      state.itemReviewsLoading = false;
      state.submitting = false;
      state.error = action.payload;
    };
    builder
      .addCase(getItemReviews.pending, (state) => {
        state.itemReviewsLoading = true;
      })
      .addCase(getItemReviews.fulfilled, (state, action) => {
        state.itemReviewsLoading = false;
        state.error = null;
        state.itemReviews = action.payload?.results || [];
        state.itemReviewsMeta = action.payload?.meta || null;
      })
      .addCase(getItemReviews.rejected, applyRejected)
      .addCase(getMyReviews.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMyReviews.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.myReviews = action.payload?.results || [];
        state.myReviewsMeta = action.payload?.meta || null;
      })
      .addCase(getMyReviews.rejected, applyRejected)
      .addCase(submitReview.pending, (state) => {
        state.submitting = true;
      })
      // Submit/delete answer with the saved review / the server-confirmed
      // flag, not a list — lists are refetched by the page that shows them.
      .addCase(submitReview.fulfilled, (state, action) => {
        state.submitting = false;
        state.error = null;
        if (action.payload?.menu_item_id) {
          state.overrides[action.payload.menu_item_id] = {
            has_review: true,
            rating: action.payload?.my_rating,
            comment: action.payload?.comment || "",
            rate: action.payload?.rate ?? null,
            reviews_count: action.payload?.reviews_count ?? 0,
          };
        }
      })
      .addCase(submitReview.rejected, applyRejected)
      .addCase(removeReview.pending, (state) => {
        state.submitting = true;
      })
      .addCase(removeReview.fulfilled, (state, action) => {
        state.submitting = false;
        state.error = null;
        if (action.payload?.menu_item_id) {
          state.overrides[action.payload.menu_item_id] = {
            has_review: false,
            rate: action.payload?.rate ?? null,
            reviews_count: action.payload?.reviews_count ?? 0,
          };
        }
      })
      .addCase(removeReview.rejected, applyRejected)
      // Logout: reviews stay saved on the server for next login; the local
      // copy is cleared so nothing leaks to the next guest on this browser.
      .addCase(logOut, (state) => {
        state.myReviews = [];
        state.myReviewsMeta = null;
        state.itemReviews = [];
        state.itemReviewsMeta = null;
        state.overrides = {};
      });
  },
});

export const { resetReviewsError } = reviewsSlice.actions;
export default reviewsSlice.reducer;
